<template>
  <div>
    <!-- Inline Error Banner -->
    <div
      v-if="errorMessage && !showSnmpModal"
      class="mb-4 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-200 px-4 py-3 rounded relative"
      role="alert"
    >
      <div class="flex items-center justify-between">
        <span class="block sm:inline">{{ errorMessage }}</span>
        <button
          @click="errorMessage = null"
          class="text-red-500 hover:text-red-700 dark:text-red-300 dark:hover:text-red-100"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>

    <!-- SNMP Configuration Modal -->
    <SNMPConfigModal
      v-if="showSnmpModal"
      :show="showSnmpModal"
      :snmp-data="selectedSnmp"
      :walk-o-i-ds="selectedWalkOIDs"
      :snmp-targets="selectedSnmpTargets"
      :is-editing="editingSnmp"
      :is-org-level="selectedSnmp?.isOrgLevel"
      :show-link-button="showLinkButton"
      :parent-type="parentType"
      :can-delete="canDeleteSnmp && !selectedSnmp?.isOrgLevel || isStaff"
      :read-only="selectedSnmp?.isOrgLevel && parentType !== 'org' || !editingSnmp"
      :errors="snmpErrors"
      :tech-types="techTypes"
      :error-message="errorMessage"
      @save="handleSnmpSave"
      @cancel="cancelSnmp"
      @delete="handleSnmpDelete"
      @link="linkOrgSnmp"
      @clear-error="clearSnmpError"
      @clear-all-errors="clearAllSnmpErrors"
      @validation-errors="handleValidationErrors"
      @clear-error-message="errorMessage = null"
    />

    <!-- Exclude (unlink) Confirmation -->
    <DeleteModal
      v-if="showExcludeConfirm"
      action="exclude"
      :is-child-object="true"
      :componet-objects="snmpToExclude ? [{ value: snmpToExclude.tech_type }] : []"
      @delete="confirmExclude"
      @close="showExcludeConfirm = false"
    />

    <!-- Header -->
    <div class="glass-card p-3  mb-4 flex items-center justify-between">
      <h2 class="text-lg font-semibold">SNMP Configurations</h2>
      
      <!-- Single Add SNMP Button -->
      <button
        v-if="canAddSnmp || isStaff"
        @click="showAddSnmpSelector"
        type="button"
        class="px-4 py-2 glass-card bg-blue-600 hover:bg-blue-700 transition-colors duration-150 flex items-center"
        title="Add SNMP Configuration"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 mr-2"
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          stroke-width="2"
        >
          <line x1="12" x2="12" y1="5" y2="19"/>
          <line x1="5" x2="19" y1="12" y2="12"/>
        </svg>
        Add SNMP
      </button>
    </div>

    <!-- Add SNMP Selector Modal -->
    <Teleport to="body">
    <div
      v-if="showAddSnmpModal"
      class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40"
      @click.self="toggleAddSnmpSelector"
    >
      <div class="glass-card max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
        <!-- Modal Header -->
        <div class="p-4 flex items-center justify-between border-b border-gray-200 dark:border-gray-700/30">
          <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-50">Add SNMP Configuration</h3>
          <button @click="toggleAddSnmpSelector" class="bg-transparent hover:bg-transparent text-red-500 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <!-- Modal Body -->
        <div class="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
          <!-- Create New Button -->
          <button
            @click="createNewSnmp"
            class="w-full p-4 mb-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-150 flex items-center justify-center font-semibold"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6 mr-2"
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2"
            >
              <line x1="12" x2="12" y1="5" y2="19"/>
              <line x1="5" x2="19" y1="12" y2="12"/>
            </svg>
            Create New {{ parentTypeLabel }}-Specific SNMP
          </button>

          <!-- Divider (only show if org SNMPs available) -->
          <div v-if="parentType !== 'org' && availableOrgSnmps.length > 0" class="relative mb-4">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-300 dark:border-gray-600"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 glass-card text-gray-700 dark:text-gray-400">
                Or link existing Global SNMP
              </span>
            </div>
          </div>

          <!-- List of Available Org SNMPs -->
          <div v-if="parentType !== 'org' && availableOrgSnmps.length > 0" class="space-y-2">
            <button
              v-for="orgSnmp in availableOrgSnmps"
              :key="orgSnmp.id"
              @click ="previewSnmp(orgSnmp)"
              class="w-full text-left p-4 glass-card transition-all duration-200 hover:bg-white/80 dark:hover:bg-surface-dark/80 hover:-translate-y-0.5"
            >
              <div class="flex items-center justify-between">
                <div>
                  <div class="font-semibold text-gray-900 dark:text-white">{{ orgSnmp.tech_type }}</div>
                  <div class="text-sm text-gray-600 dark:text-gray-400">
                    Community: {{ orgSnmp.community_string }}
                  </div>
                  <div class="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {{ (orgSnmp.walk_oids || []).length }} walk OID(s) configured
                  </div>
                </div>
                <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </div>
            </button>
          </div>

        </div>
      </div>
    </div>
    </Teleport>

    <!-- Permission denied -->
    <div v-if="!canViewSnmp && !isStaff" class="text-center py-12 text-gray-500 dark:text-gray-400">
      You don't have permission to view SNMP configurations.
    </div>

    <!-- No SNMPs Empty State -->
    <div v-else-if="snmps.length === 0" class="text-center py-12">
      <ServerIcon class="mx-auto h-16 w-16 text-gray-400" />
      <p class="mt-4 text-lg text-gray-500 dark:text-gray-400">No SNMP configurations</p>
      <p class="mt-2 text-sm text-gray-400 dark:text-gray-500">
        Add an SNMP configuration to get started
      </p>
    </div>

    <!-- SNMP Cards Display -->
    <div v-else>
      <!-- Linked Org SNMPs Section -->
      <div ref="globalSectionRef" v-if="linkedOrgSnmps.length > 0" class="mb-6 scroll-mt-24">
        <button
          type="button"
          @click="toggleGlobal"
          :aria-expanded="!globalCollapsed"
          class="w-full text-left text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center justify-between hover:text-gray-900 dark:hover:text-white transition-all duration-200 glass-card py-3 hover:bg-white dark:hover:bg-surface-dark transform hover:-translate-y-0.5"
        >
          <span class="flex items-center">
            <svg class="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/>
            </svg>
            Global SNMP Configurations
            <span class="ml-2 text-xs text-gray-500 dark:text-gray-400 font-normal">({{ linkedOrgSnmps.length }})</span>
          </span>
          <svg class="w-4 h-4 transition-transform duration-200" :class="{ 'rotate-180': !globalCollapsed }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <Transition name="snmp-collapse">
        <div v-if="!globalCollapsed" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div
            v-for="snmp in linkedOrgSnmps"
            :key="'org-' + snmp.id"
            @click="viewSnmp(snmp)"
            class="glass-card-lite"
          >
            <!-- Card Header -->
            <div class="glass-card-lite p-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <h3 class="text-lg font-semibold">{{ snmp.tech_type }}</h3>
                  <span class="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-500 text-white">
                    Global
                  </span>
                </div>
                <div class="flex gap-1">
                  <!-- Unlink Button (X icon) -->
                  <button
                    @click.stop="promptExclude(snmp)"
                    class="p-1 bg-transparent hover:bg-red-600 rounded transition-colors duration-150 text-gray-500 dark:text-gray-400 hover:text-white"
                    :title="`Exclude from this ${parentTypeLabel}`"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Card Body -->
            <div class="p-4 space-y-2">
              <div class="flex items-center justify-between text-sm gap-2">
                <span>Community:</span>
                <span class="font-mono font-medium text-gray-900 dark:text-white truncate">{{ snmp.community_string }}</span>
              </div>

              <div v-if="isStaff" class="flex items-center justify-between text-sm" title="Scrape interval / timeout">
                <span>Scrapes:</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ snmp.scrape_interval }}s / {{ snmp.scrape_timeout }}s</span>
              </div>

              <div class="flex items-center justify-between text-sm">
                <span>Walk OIDs:</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ getWalkOIDsForSnmp(snmp.id).length }}</span>
              </div>

              <div class="flex items-center justify-between text-sm">
                <span>Targets:</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ snmp.snmp_targets?.length || 0 }}</span>
              </div>
            </div>
          </div>
        </div>
        </Transition>
      </div>

      <!-- Local/Owned SNMPs Section -->
      <div ref="localSectionRef" v-if="ownedSnmps.length > 0" class="scroll-mt-24">
        <button
          type="button"
          @click="toggleLocal"
          :aria-expanded="!localCollapsed"
          class="w-full text-left text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center justify-between hover:text-gray-900 dark:hover:text-white transition-all duration-200 glass-card py-3 hover:bg-white dark:hover:bg-surface-dark transform hover:-translate-y-0.5"
        >
          <span class="flex items-center">
            <svg class="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"/>
            </svg>
            {{ parentTypeLabel }}-Specific SNMP Configurations
            <span class="ml-2 text-xs text-gray-500 dark:text-gray-400 font-normal">({{ ownedSnmps.length }})</span>
          </span>
          <svg class="w-4 h-4 transition-transform duration-200" :class="{ 'rotate-180': !localCollapsed }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <Transition name="snmp-collapse">
        <div v-if="!localCollapsed" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div
            v-for="snmp in ownedSnmps"
            :key="snmp.id"
            @click="canChangeSnmp ? editSnmp(snmp) : viewSnmp(snmp)"
            class="glass-card-lite"
          >
            <!-- Card Header -->
            <div class="glass-card-lite p-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <h3 class="text-lg font-semibold">{{ snmp.tech_type }}</h3>
                  <span class="text-xs font-medium px-2 py-0.5 rounded-full"
                    :class="{
                      'bg-indigo-500': parentType === 'org',
                      'bg-emerald-500': parentType === 'site',
                      'bg-violet-500': parentType === 'probe'
                    }"
                  >
                    {{ parentTypeLabel }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Card Body -->
            <div class="p-4 space-y-2">
              <div class="flex items-center justify-between text-sm gap-2">
                <span>Community:</span>
                <span class="font-mono font-medium text-gray-900 dark:text-white truncate">{{ snmp.community_string }}</span>
              </div>

              <div v-if="isStaff" class="flex items-center justify-between text-sm" title="Scrape interval / timeout">
                <span>Scrapes:</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ snmp.scrape_interval }}s / {{ snmp.scrape_timeout }}s</span>
              </div>

              <div class="flex items-center justify-between text-sm">
                <span>Walk OIDs:</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ getWalkOIDsForSnmp(snmp.id).length }}</span>
              </div>

              <div class="flex items-center justify-between text-sm">
                <span>Targets:</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ snmp.snmp_targets?.length || 0 }}</span>
              </div>
            </div>
          </div>
        </div>
        </Transition>
      </div>


    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { ServerIcon } from '@heroicons/vue/24/outline';
import SNMPConfigModal from '@/components/SNMPConfigModal.vue';
import DeleteModal from '@/components/DeleteModal.vue';
import { useSnmpManagement } from '@/composables/useSnmpManagement';
import { useStore } from '@/store';

const globalCollapsed = ref(false);
const localCollapsed = ref(false);
const globalSectionRef = ref(null);
const localSectionRef = ref(null);

function toggleGlobal() {
  globalCollapsed.value = !globalCollapsed.value;
  if (!globalCollapsed.value) {
    nextTick(() => globalSectionRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }
}

function toggleLocal() {
  localCollapsed.value = !localCollapsed.value;
  if (!localCollapsed.value) {
    nextTick(() => localSectionRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }
}

const showExcludeConfirm = ref(false);
const snmpToExclude = ref(null);

function promptExclude(snmp) {
  snmpToExclude.value = snmp;
  showExcludeConfirm.value = true;
}

async function confirmExclude() {
  const snmp = snmpToExclude.value;
  showExcludeConfirm.value = false;
  snmpToExclude.value = null;
  if (snmp) await unlinkOrgSnmp(snmp.id);
}

const props = defineProps({
  parentType: {
    type: String,
    required: true,
    validator: (value) => ['site', 'probe', 'org'].includes(value)
  },
  parentId: {
    type: Number,
    required: true
  },
  orgId: {
    type: Number,
    default: null
  },
  schemaName: {
    type: String,
    default: null 
  }
});

const {
  errorMessage,
  availableOrgSnmps,
  isStaff,
  canAddSnmp,
  canChangeSnmp,
  showAddSnmpModal,
  showLinkButton,
  toggleAddSnmpSelector,
  showAddSnmpSelector,
  createNewSnmp,
  viewSnmp,
  previewSnmp,
  linkOrgSnmp,
  unlinkOrgSnmp,
  getWalkOIDsForSnmp,
  snmps,
  linkedOrgSnmps,
  ownedSnmps,
  editSnmp,
  showSnmpModal,           
  selectedSnmp,            
  selectedWalkOIDs,
  selectedSnmpTargets,        
  editingSnmp,             
  canDeleteSnmp,           
  snmpErrors,              
  techTypes,               
  handleSnmpSave,          
  cancelSnmp,              
  handleSnmpDelete,
  clearSnmpError,          
  clearAllSnmpErrors,      
  handleValidationErrors,  
  fetchSnmps,
  canViewSnmp,
} = useSnmpManagement(props.parentType, computed(() => props.parentId), computed(() => props.orgId), props.schemaName);

const parentTypeLabel = computed(() =>
  ({ org: 'Global', site: 'Site', probe: 'Probe' })[props.parentType]
);

onMounted(async () => {
  if (canViewSnmp.value || isStaff.value) {
    await fetchSnmps();
  }
});
</script>

<style scoped>
.snmp-collapse-enter-active,
.snmp-collapse-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.snmp-collapse-enter-from,
.snmp-collapse-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
